import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import { Box, Paper, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Card, CircularProgress, Alert } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import ChairOutlinedIcon from "@mui/icons-material/ChairOutlined";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import CountertopsOutlinedIcon from "@mui/icons-material/CountertopsOutlined";
import ShowerOutlinedIcon from "@mui/icons-material/ShowerOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropertyDetailCard from "../../components/cards/PropertyDetailcard";
import PropertyImageGallery from "../../components/PropertyImageGallery";
import { FlexCenter, FlexStart } from "../../components/FlexBox";
import { api } from "../../services/API";

const facilityIcons = {
	marginRight: "8px",
	fontSize: "20px",
	color: "#52524e",
};

const PropertyPage = () => {
	const [searchParams] = useSearchParams();
	const propertyId = searchParams.get("id");
	
	const [openDialog, setOpenDialog] = useState(false);
	const [phoneNumber, setPhoneNumber] = useState("");
	const [userDetails, setUserDetails] = useState(null);
	const [dialogTitle, setDialogTitle] = useState(""); // To handle different dialog titles

	// Tour booking states
	const [openTourDialog, setOpenTourDialog] = useState(false);
	const [tourBookingData, setTourBookingData] = useState({
		visitor_name: "",
		phone_number: "",
		email: "",
		desired_visit_date: null,
		desired_visit_time: null,
		desired_move_in_date: null,
		notes: ""
	});
	const [submittingTour, setSubmittingTour] = useState(false);
	const [tourBookingError, setTourBookingError] = useState("");
	const [tourBookingSuccess, setTourBookingSuccess] = useState(false);

	// Property data states
	const [propertyData, setPropertyData] = useState(null);
	const [loadingProperty, setLoadingProperty] = useState(true);
	const [propertyError, setPropertyError] = useState(null);

	// Define the missing states
	const [loadedTenant, setLoadedTenant] = useState(false);
	const [isExisitingTenant, setIsExisitingTenant] = useState(false)
	const [isLoadedUnits, setIsLoadedUnits] = useState(false)
	const [loadingTenantsError, setLoadingTenantsError] = useState("")
	const [tenantNumber, setTenantNumber] = useState("")
	const [tenantDetails, setTenantDetails] = useState(null)
	const [loadingTenantDetails, setLoadingTenantDetails] = useState(false);
	const [errorLoadingTenantDetails, setErrorLoadingTenantDetails] = useState(false)
	const [showRegistrationForm, setShowRegistrationForm] = useState(false)

	// Fetch property details
	useEffect(() => {
		if (!propertyId) {
			setPropertyError("Property ID is missing");
			setLoadingProperty(false);
			return;
		}

		const fetchPropertyDetails = async () => {
			setLoadingProperty(true);
			setPropertyError(null);
			try {
				const response = await api.get(`/api/v1/tenants/houses/${propertyId}`);
				if (response.data && response.data.status === 200 && response.data.data && response.data.data.length > 0) {
					setPropertyData(response.data.data[0]);
				} else {
					setPropertyError("Property not found");
				}
			} catch (error) {
				setPropertyError(error.message || "Failed to load property details");
			} finally {
				setLoadingProperty(false);
			}
		};

		fetchPropertyDetails();
	}, [propertyId]);

	const handleOpenDialog = (title) => {
		setDialogTitle(title);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false)
		setTenantDetails(null)
	};

	const handleOpenTourDialog = () => {
		setOpenTourDialog(true);
		setTourBookingError("");
		setTourBookingSuccess(false);
		setTourBookingData({
			visitor_name: "",
			phone_number: "",
			email: "",
			desired_visit_date: null,
			desired_visit_time: null,
			desired_move_in_date: null,
			notes: ""
		});
	};

	const handleCloseTourDialog = () => {
		setOpenTourDialog(false);
		setTourBookingError("");
		setTourBookingSuccess(false);
		setTourBookingData({
			visitor_name: "",
			phone_number: "",
			email: "",
			desired_visit_date: null,
			desired_visit_time: null,
			desired_move_in_date: null,
			notes: ""
		});
	};

	const handleTourBookingChange = (field, value) => {
		setTourBookingData(prev => ({
			...prev,
			[field]: value
		}));
	};

	const handleSubmitTourBooking = async () => {
		if (!tourBookingData.visitor_name || !tourBookingData.phone_number || !tourBookingData.email) {
			setTourBookingError("Name, phone number, and email are required");
			return;
		}

		if (!propertyId) {
			setTourBookingError("Property ID is missing");
			return;
		}

		// Extract related_rental from propertyData
		// Based on API response: propertyData.related_rental is the rental ID
		// Or we can get it from propertyData.related_rental_unit.related_rental.id
		const related_rental = propertyData?.related_rental || propertyData?.related_rental_unit?.related_rental?.id;

		if (!related_rental) {
			setTourBookingError("Rental information is missing");
			return;
		}

		setSubmittingTour(true);
		setTourBookingError("");
		
		try {
			const payload = {
				related_rental_unit: propertyId,
				related_rental: related_rental,
				visitor_name: tourBookingData.visitor_name,
				phone_number: tourBookingData.phone_number,
				email: tourBookingData.email,
				desired_visit_date: tourBookingData.desired_visit_date ? tourBookingData.desired_visit_date.format("YYYY-MM-DD") : null,
				desired_visit_time: tourBookingData.desired_visit_time ? tourBookingData.desired_visit_time.format("HH:mm") : null,
				desired_move_in_date: tourBookingData.desired_move_in_date ? tourBookingData.desired_move_in_date.format("YYYY-MM-DD") : null,
				notes: tourBookingData.notes || null
			};

			const response = await api.post(`/api/v1/tenants/houses/visit-booking`, payload);
			
			if (response.data && response.data.status === 201) {
				setTourBookingSuccess(true);
				setTourBookingError("");
				// Close dialog after 3 seconds
				setTimeout(() => {
					handleCloseTourDialog();
				}, 3000);
			} else {
				setTourBookingError(response.data?.message || "Failed to book tour");
			}
		} catch (error) {
			setTourBookingError(error.response?.data?.message || error.message || "Failed to book tour. Please try again.");
		} finally {
			setSubmittingTour(false);
		}
	};

	const handlePhoneNumberChange = (event) => setPhoneNumber(event.target.value);

	const handleSubmit = async () => {
		// Placeholder for backend call
	};

	const handleChangeTenantNumber = async (e) => {
		setLoadedTenant(false);
		setShowRegistrationForm(false)
		setIsExisitingTenant(true);
		setIsLoadedUnits(false);
		setLoadingTenantsError("");
		const tenant_number = e.target.value;
		if (tenant_number.length === 13) {
			setTenantNumber(tenant_number);
			try {
				const response = await api.post(`https://api.rentbeta.iolabz.ug/api/v1/accounts/users/details`,
					 {
						phone_number: tenant_number
					 }
					)
				if(response.data.data){
					setLoadedTenant(true)
					setTenantDetails(response.data.data)
				}
			
				setLoadedTenant(true);
				setLoadingTenantDetails(false);
				setLoadingTenantsError("");
				return true;
			} catch (err) {
				setShowRegistrationForm(true)
				setIsExisitingTenant(false);
				setLoadingTenantDetails(false);
				setErrorLoadingTenantDetails(true);
				setLoadingTenantsError("");
				return false;
			}
		} else {
			setLoadingTenantsError("Please type a complete phone number with 12 digits including 256");
		}
	};

	// Show loading state
	if (loadingProperty) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
				<CircularProgress />
			</Box>
		);
	}

	// Show error state
	if (propertyError || !propertyData) {
		return (
			<Box sx={{ padding: "20px", textAlign: "center" }}>
				<Typography variant="h6" color="error">
					{propertyError || "Property not found"}
				</Typography>
			</Box>
		);
	}

	// Extract property data
	const unit = propertyData.related_rental_unit || {};
	const rental = unit.related_rental || {};
	const images = propertyData.images || [];
	const metadata = propertyData;

	// Prepare images for gallery (use rentbeta logo if no images)
	const galleryImages = images.length > 0 
		? images.map(img => ({ src: img.image }))
		: [{ src: "/images/rent-beta.png" }];

	return (
		<>
			<Grid container spacing={4}>
				<Grid xs={12} sm={8}>
					<Stack spacing={3} sx={{ marginBottom: "20px" }}>
						<PropertyImageGallery images={galleryImages} />
						<Box sx={{ border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "4px", padding: "1rem" }}>
							<Typography variant="body1" sx={{ marginRight: "3px" }}>
								{unit.description || "No description available for this property."}
							</Typography>
						</Box>
						<Typography variant="h5">Facilities</Typography>
						<Box sx={{ border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "4px", padding: "1rem" }}>
							<Grid container spacing={2}>
								<Grid xs={12} sm={6}>
									<Stack spacing={2}>
										<FlexCenter>
											<BedOutlinedIcon sx={facilityIcons} />
											<Typography>{unit.number_of_bedrooms || 0} Bedroom{unit.number_of_bedrooms !== 1 ? 's' : ''}</Typography>
										</FlexCenter>
										<FlexCenter>
											<ShowerOutlinedIcon sx={facilityIcons} />
											<Typography>{unit.number_of_bathrooms || 0} Bathroom{unit.number_of_bathrooms !== 1 ? 's' : ''}</Typography>
										</FlexCenter>
										<FlexCenter>
											<CountertopsOutlinedIcon sx={facilityIcons} />
											<Typography>Kitchen {unit.has_inside_kitchen ? "(Inside)" : "(Outside)"}</Typography>
										</FlexCenter>
										{unit.is_fully_furnished && (
											<FlexCenter>
												<ChairOutlinedIcon sx={facilityIcons} />
												<Typography>Fully Furnished</Typography>
											</FlexCenter>
										)}
									</Stack>
								</Grid>
								<Grid xs={12} sm={6}>
									<Stack spacing={2}>
										{rental.has24_security && (
											<FlexCenter>
												<LockResetOutlinedIcon sx={facilityIcons} />
												<Typography>24/7 Security</Typography>
											</FlexCenter>
										)}
										{(rental.has_fibre || unit.has_fibre_connection) && (
											<FlexCenter>
												<WifiOutlinedIcon sx={facilityIcons} />
												<Typography>Internet/Fibre</Typography>
											</FlexCenter>
										)}
										{rental.has_gym && (
											<FlexCenter>
												<FitnessCenterOutlinedIcon sx={facilityIcons} />
												<Typography>Gym</Typography>
											</FlexCenter>
										)}
										{rental.has_parking && (
											<FlexCenter>
												<DirectionsCarFilledOutlinedIcon sx={facilityIcons} />
												<Typography>Parking Space</Typography>
											</FlexCenter>
										)}
										{rental.has_swimming_pool && (
											<FlexCenter>
												<Typography>Swimming Pool</Typography>
											</FlexCenter>
										)}
									</Stack>
								</Grid>
							</Grid>
						</Box>
						<Typography variant="h5">Contract Type & Terms</Typography>
						<Box sx={{ border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "4px", padding: "1rem" }}>
							<Stack spacing={3}>
								<FlexStart>
									<EventAvailableOutlinedIcon sx={{ fontSize: "22px", marginRight: "5px" }} />
									<Stack spacing={1}>
										<Typography variant="h5" sx={{ fontSize: "18px" }}>
											{metadata.unit_rent_cycle || "MONTHLY"}
										</Typography>
										<Typography>You will be required to pay the full {metadata.unit_rent_cycle?.toLowerCase() || "month"}'s rent at every start of a {metadata.unit_rent_cycle?.toLowerCase() || "month"} cycle no matter when you move-in</Typography>
									</Stack>
								</FlexStart>
								{metadata.has_down_payment && (
									<FlexStart>
										<PaymentsOutlinedIcon sx={{ fontSize: "22px", marginRight: "5px" }} />
										<Stack spacing={1}>
											<Typography variant="h5" sx={{ fontSize: "18px" }}>
												Advanced Payment
											</Typography>
											<Typography>
												You will be required to pay an advance of {parseFloat(metadata.unit_cycle_down_payment || 0).toLocaleString()} {metadata.rent_currency || "UGX"} 
												{metadata.down_payment_periods_covered > 1 
													? ` (${metadata.down_payment_periods_covered} ${metadata.unit_rent_cycle?.toLowerCase() || "month"}s)` 
													: ` (${metadata.down_payment_periods_covered} ${metadata.unit_rent_cycle?.toLowerCase() || "month"})`} before moving in
											</Typography>
										</Stack>
									</FlexStart>
								)}
								{metadata.has_security_fee && (
									<FlexStart>
										<PaymentsOutlinedIcon sx={{ fontSize: "22px", marginRight: "5px" }} />
										<Stack spacing={1}>
											<Typography variant="h5" sx={{ fontSize: "18px" }}>
												Security Fee
											</Typography>
											<Typography>
												Security fee of {parseFloat(metadata.unit_rent_security_fee || 0).toLocaleString()} {metadata.rent_currency || "UGX"} is required
											</Typography>
										</Stack>
									</FlexStart>
								)}
								<FlexStart>
									<BadgeOutlinedIcon sx={{ fontSize: "22px", marginRight: "5px" }} />
									<Stack spacing={1}>
										<Typography variant="h5" sx={{ fontSize: "18px" }}>
											Proof of identity
										</Typography>
										<Typography>Government issued ID, passport or driver's license.</Typography>
									</Stack>
								</FlexStart>
							</Stack>
						</Box>
						<Typography variant="h5">How to book this property?</Typography>
						<Box>
							<Accordion>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>I like this property, how do I book it?</AccordionSummary>
								<AccordionDetails>
									<Stack spacing={2}>
										<Typography>To book this property, please schedule a tour first. Click the button below to book a tour and our team will get in touch with you to proceed with the booking process.</Typography>
										<Button onClick={handleOpenTourDialog} variant="contained" color="primary">
											Book a Tour
										</Button>
									</Stack>
								</AccordionDetails>
							</Accordion>
						</Box>
					</Stack>
				</Grid>
				<Grid xs={12} sm={4}>
					<Stack spacing={2}>
						<PropertyDetailCard 
							rent={parseFloat(metadata.unit_rent || 0)}
							currency={metadata.rent_currency || "UGX"}
							rentCycle={metadata.unit_rent_cycle || "MONTHLY"}
							unitName={unit.unit_name}
							rentalName={rental.rental_name}
							location={rental.location}
							onBookTour={handleOpenTourDialog}
						/>
					</Stack>
				</Grid>
			</Grid>
			<Dialog open={openDialog} onClose={handleCloseDialog}>
				<DialogTitle>{dialogTitle}</DialogTitle>
				<DialogContent  item xs={12} spacing={2} sx={{marginTop: 2}}>
					<Typography variant="body1"> Enter the Tenants Phone number starting with 256, for example 256770123456
					</Typography>
					<TextField
						required
						id="outlined-required"
						label="Tenants Phone Number"
						onChange={handleChangeTenantNumber}
						fullWidth={true}
						variant="standard"
						margin="dense"
						autoFocus
					/>
					
		{loadingTenantsError && (
        <Typography variant="h6" component="div" sx={{color:"red"}}>
          {loadingTenantsError}
        </Typography>
      )}
	{(tenantDetails && loadedTenant) &&
	<Card>
				<p> Full name: {tenantDetails.first_name} {tenantDetails.last_name}</p>
				<p> PhoneNumber: {tenantDetails.username} </p>
				</Card>
	}
	{showRegistrationForm &&
	<Grid container spacing={2} sx={{ marginTop: 2 }}>
	<Grid item xs={12} sm={6}>
	  <TextField
		required
		id="outlined-required"
		label="Enter the tenant phone number"
		fullWidth
		variant="standard"
		margin="dense"
	  />
	</Grid>
	<Grid item xs={12} sm={6}>
	  <TextField
		required
		id="outlined-required"
		label="Enter the tenant email"
		fullWidth
		variant="standard"
		margin="dense"
	  />
	</Grid>
  </Grid>
  
			}



				</DialogContent>

				
				<DialogActions>
					<Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
						<Button onClick={handleCloseDialog} variant="contained" color="primary">
							Cancel
						</Button>
						<Button onClick={handleSubmit} variant="contained" color="primary">
							Submit
						</Button>
					</Box>
				</DialogActions>
			</Dialog>

			{/* Tour Booking Dialog */}
			<Dialog open={openTourDialog} onClose={handleCloseTourDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Book a Tour</DialogTitle>
				<DialogContent>
					<Stack spacing={3} sx={{ marginTop: 2 }}>
						{tourBookingSuccess && (
							<Alert severity="success">
								Visit booking created successfully! We'll contact you soon.
							</Alert>
						)}
						{tourBookingError && (
							<Alert severity="error">
								{tourBookingError}
							</Alert>
						)}
						<TextField
							required
							fullWidth
							label="Full Name"
							value={tourBookingData.visitor_name}
							onChange={(e) => handleTourBookingChange("visitor_name", e.target.value)}
							variant="outlined"
							margin="dense"
							disabled={submittingTour || tourBookingSuccess}
						/>
						<TextField
							required
							fullWidth
							label="Phone Number"
							value={tourBookingData.phone_number}
							onChange={(e) => handleTourBookingChange("phone_number", e.target.value)}
							variant="outlined"
							margin="dense"
							placeholder="256770123456"
							disabled={submittingTour || tourBookingSuccess}
						/>
						<TextField
							required
							fullWidth
							label="Email"
							type="email"
							value={tourBookingData.email}
							onChange={(e) => handleTourBookingChange("email", e.target.value)}
							variant="outlined"
							margin="dense"
							disabled={submittingTour || tourBookingSuccess}
						/>
						<DatePicker
							label="Desired Visit Date"
							value={tourBookingData.desired_visit_date}
							onChange={(date) => handleTourBookingChange("desired_visit_date", date)}
							slotProps={{
								textField: {
									fullWidth: true,
									variant: "outlined",
									margin: "dense",
									disabled: submittingTour || tourBookingSuccess
								}
							}}
						/>
						<TimePicker
							label="Desired Visit Time"
							value={tourBookingData.desired_visit_time}
							onChange={(time) => handleTourBookingChange("desired_visit_time", time)}
							slotProps={{
								textField: {
									fullWidth: true,
									variant: "outlined",
									margin: "dense",
									disabled: submittingTour || tourBookingSuccess
								}
							}}
						/>
						<DatePicker
							label="Desired Move In Date"
							value={tourBookingData.desired_move_in_date}
							onChange={(date) => handleTourBookingChange("desired_move_in_date", date)}
							slotProps={{
								textField: {
									fullWidth: true,
									variant: "outlined",
									margin: "dense",
									disabled: submittingTour || tourBookingSuccess
								}
							}}
						/>
						<TextField
							fullWidth
							label="Notes (Optional)"
							multiline
							rows={4}
							value={tourBookingData.notes}
							onChange={(e) => handleTourBookingChange("notes", e.target.value)}
							variant="outlined"
							margin="dense"
							placeholder="Any additional information or questions..."
							disabled={submittingTour || tourBookingSuccess}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 16px" }}>
						<Button 
							onClick={handleCloseTourDialog} 
							variant="outlined" 
							disabled={submittingTour}
							sx={{ borderWidth: 2 }}
						>
							Cancel
						</Button>
						<Button 
							onClick={handleSubmitTourBooking} 
							variant="contained" 
							color="primary"
							disabled={submittingTour || tourBookingSuccess}
						>
							{submittingTour ? "Submitting..." : "Submit Booking Request"}
						</Button>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default PropertyPage;
